import { jsPDF } from 'jspdf';

function layoutRectToCanvasY(
  layoutY: number,
  layoutH: number,
  canvasH: number
): number {
  if (layoutH <= 0) return 0;
  return Math.round((layoutY / layoutH) * canvasH);
}

function collectPdfSliceBounds(receipt: HTMLElement): { top: number; bottom: number }[] {
  const rows = Array.from(
    receipt.querySelectorAll<HTMLElement>('.receipt__pdf-slice')
  );
  if (rows.length === 0) {
    return [];
  }
  const rb = receipt.getBoundingClientRect();
  const st = receipt.scrollTop;
  return rows
    .map((el) => {
      const er = el.getBoundingClientRect();
      return {
        top: er.top - rb.top + st,
        bottom: er.bottom - rb.top + st,
      };
    })
    .filter((b) => b.bottom > b.top)
    .sort((a, b) => a.top - b.top);
}

/** Greedy packing: each page fits whole slices only; never ends mid-slice. */
function computeRowAlignedSegments(
  layoutH: number,
  layoutW: number,
  pageHeightMm: number,
  pageWidthMm: number,
  slices: { top: number; bottom: number }[]
): { y0: number; y1: number }[] {
  const pageHeightLayout = layoutW * (pageHeightMm / pageWidthMm);
  const segments: { y0: number; y1: number }[] = [];
  let cursor = 0;
  let i = 0;

  while (cursor < layoutH - 0.5) {
    const limit = cursor + pageHeightLayout;
    let pageBottom = cursor;

    while (i < slices.length) {
      const s = slices[i];
      if (s.bottom <= cursor + 0.5) {
        i++;
        continue;
      }
      if (s.top >= limit - 0.5) {
        break;
      }
      if (s.bottom <= limit + 0.5) {
        pageBottom = Math.max(pageBottom, s.bottom);
        i++;
      } else {
        if (pageBottom > cursor + 0.5) {
          break;
        }
        pageBottom = s.bottom;
        i++;
        break;
      }
    }

    if (pageBottom <= cursor + 0.5) {
      pageBottom = Math.min(limit, layoutH);
    }

    segments.push({ y0: cursor, y1: pageBottom });
    cursor = pageBottom;
  }

  return segments;
}

/**
 * Splits canvas using fixed-height strips (may cut through text).
 * Use {@link addCanvasAsPdfPages} with receiptRoot for receipt PDFs.
 */
export function addCanvasAsPdfPagesFixedSlices(
  pdf: jsPDF,
  sourceCanvas: HTMLCanvasElement,
  options?: { pageWidthMm?: number; pageHeightMm?: number }
): void {
  const pageWidthMm = options?.pageWidthMm ?? 210;
  const pageHeightMm = options?.pageHeightMm ?? 297;

  const wPx = sourceCanvas.width;
  const hPx = sourceCanvas.height;
  if (wPx <= 0 || hPx <= 0) {
    return;
  }

  const fullHeightMm = (hPx * pageWidthMm) / wPx;
  const pxPerMm = hPx / fullHeightMm;
  const slicePx = Math.max(1, Math.floor(pageHeightMm * pxPerMm));

  let yPx = 0;
  let isFirstPage = true;

  while (yPx < hPx) {
    const sliceH = Math.min(slicePx, hPx - yPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = wPx;
    sliceCanvas.height = sliceH;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) {
      break;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, wPx, sliceH);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(sourceCanvas, 0, yPx, wPx, sliceH, 0, 0, wPx, sliceH);

    const sliceMm = (sliceH / hPx) * fullHeightMm;

    if (!isFirstPage) {
      pdf.addPage();
    }
    isFirstPage = false;
    pdf.addImage(
      sliceCanvas.toDataURL('image/png'),
      'PNG',
      0,
      0,
      pageWidthMm,
      sliceMm
    );

    yPx += sliceH;
  }
}

export function addCanvasAsPdfPagesRowAligned(
  pdf: jsPDF,
  sourceCanvas: HTMLCanvasElement,
  receipt: HTMLElement,
  options?: { pageWidthMm?: number; pageHeightMm?: number }
): void {
  const pageWidthMm = options?.pageWidthMm ?? 210;
  const pageHeightMm = options?.pageHeightMm ?? 297;

  const wPx = sourceCanvas.width;
  const hPx = sourceCanvas.height;
  if (wPx <= 0 || hPx <= 0) {
    return;
  }

  const layoutH = receipt.scrollHeight;
  const layoutW = receipt.scrollWidth;
  if (layoutH <= 0 || layoutW <= 0) {
    addCanvasAsPdfPagesFixedSlices(pdf, sourceCanvas, options);
    return;
  }

  const sliceBounds = collectPdfSliceBounds(receipt);
  const fullHeightMm = (hPx * pageWidthMm) / wPx;

  let segments: { y0: number; y1: number }[];
  if (sliceBounds.length === 0) {
    segments = computeRowAlignedSegments(
      layoutH,
      layoutW,
      pageHeightMm,
      pageWidthMm,
      [{ top: 0, bottom: layoutH }]
    );
  } else {
    segments = computeRowAlignedSegments(
      layoutH,
      layoutW,
      pageHeightMm,
      pageWidthMm,
      sliceBounds
    );
  }

  let isFirst = true;
  for (const { y0, y1 } of segments) {
    const sy0 = Math.max(0, layoutRectToCanvasY(y0, layoutH, hPx));
    const sy1 = Math.min(hPx, layoutRectToCanvasY(y1, layoutH, hPx));
    const sh = sy1 - sy0;
    if (sh <= 0) {
      continue;
    }

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = wPx;
    sliceCanvas.height = sh;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) {
      continue;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, wPx, sh);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(sourceCanvas, 0, sy0, wPx, sh, 0, 0, wPx, sh);

    const sliceMm = (sh / hPx) * fullHeightMm;

    if (!isFirst) {
      pdf.addPage();
    }
    isFirst = false;
    pdf.addImage(
      sliceCanvas.toDataURL('image/png'),
      'PNG',
      0,
      0,
      pageWidthMm,
      sliceMm
    );
  }
}

/**
 * Receipt PDFs: pass `receiptRoot` so page breaks align to `.receipt__pdf-slice` rows.
 */
export function addCanvasAsPdfPages(
  pdf: jsPDF,
  sourceCanvas: HTMLCanvasElement,
  options?: {
    pageWidthMm?: number;
    pageHeightMm?: number;
    receiptRoot?: HTMLElement | null;
  }
): void {
  if (options?.receiptRoot) {
    addCanvasAsPdfPagesRowAligned(pdf, sourceCanvas, options.receiptRoot, {
      pageWidthMm: options.pageWidthMm,
      pageHeightMm: options.pageHeightMm,
    });
  } else {
    addCanvasAsPdfPagesFixedSlices(pdf, sourceCanvas, options);
  }
}
